import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Container, Spinner, Alert } from 'react-bootstrap';
import { useLazyQuery } from '@apollo/client';
import { GET_INVENTORY_BY_ID } from 'api/graphql/queries/inventory/getInventoryById';
import { updateInventoryRequest } from 'services/inventoryService';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBoxOpen, faShippingFast, faFile, faCheck, faBox, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import { SELLER_PRODUCTS_QUERY } from 'api/graphql/queries/product/GetInvProducts';
import EditInventoryRequestModal from 'components/modules/e-commerce/products/EditInventoryRequestModal';

// Components
import InventoryHeader from 'components/modules/e-commerce/inventory/InventoryHeader';
import StatusCard from 'components/modules/e-commerce/inventory/StatusCard';
import DateInfoCard from 'components/modules/e-commerce/inventory/DateInfoCard';
import ProductsListCard from 'components/modules/e-commerce/inventory/ProductsListCard';
import ActionButtons from 'components/modules/e-commerce/inventory/ActionButtons';

interface InventoryItem {
  ID: string;
  Qty: string;
  product_id?: string;
  quantity_done?: number;
  productName?: string;
  quantity?: number;
  imageUrl?: string;
  currentStock?: number;
  seller_reference?: string;
}


interface InventoryDetails {
  inv_id: string;
  status: string;
  "date prévue"?: string;
  date_demande?: string;
  date_prevue?: string;
  date_done?: string;
  move_type?: string;
  stock_type?: string;
  inventory_list: InventoryItem[];
}

interface ProductOption {
  value: string;
  label: string;
  image: string;
  seller_reference: string;
  stockQty: number;
}

const InventoryDetailsPage: React.FC = () => {
  const { t } = useTranslation();
  // Get the inventory ID directly as it's in the route path
  const params = useParams<{ inventoryId: string }>();
  const inventoryId = params.inventoryId;
  console.log('Inventory ID from URL params:', inventoryId);
  
  const navigate = useNavigate();

  // States
  const [inventory, setInventory] = useState<InventoryDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [productDetails, setProductDetails] = useState<Record<string, any>>({});
  const [productsLoading, setProductsLoading] = useState(false);
  
  // GraphQL queries
  const [fetchProducts, { loading: queryLoading }] = useLazyQuery(SELLER_PRODUCTS_QUERY);
  const [getInventoryById, { loading: inventoryLoading, error: inventoryError, data: inventoryData }] = useLazyQuery(GET_INVENTORY_BY_ID);
  
  // Fetch products by IDs
  const fetchProductDetails = useCallback(async (productIds: string[]) => {
    if (!productIds || productIds.length === 0) return;
    
    setProductsLoading(true);
    try {
      // Convert string IDs to integers and filter out invalid ones
      const numericIds = productIds.map(id => parseInt(id, 10)).filter(id => !isNaN(id));
      
      if (numericIds.length === 0) {
        console.warn("No valid product IDs to fetch");
        setProductsLoading(false);
        return;
      }
      
      const { data } = await fetchProducts({
        variables: {
          page: 1,
          pageSize: 100, // Set a large enough limit to get all products
          filter: {
            ids: numericIds
          }
        }
      });
      
      // Create a map of productId -> productData for easy lookup
      const productsMap: Record<string, any> = {};
      
      if (data?.sellerProducts?.products && Array.isArray(data.sellerProducts.products)) {
        data.sellerProducts.products.forEach((product: any) => {
          if (product?.entityid) {
            productsMap[product.entityid.toString()] = {
              id: product.entityid.toString(),
              name: product.name || `Product ${product.entityid}`,
              seller_reference: product.seller_reference || '',
              imageUrl: product.image1?.url || 'https://via.placeholder.com/50',
              stockQty: product.stock_data?.qty || 0,
            };
          }
        });
      }
      
      console.log('Fetched product details:', productsMap);
      setProductDetails(productsMap);
    } catch (error) {
      console.error("Error fetching product details:", error);
      toast.error("Failed to load product details");
    } finally {
      setProductsLoading(false);
    }
  }, [fetchProducts]);

  // Format date helper
  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const options: Intl.DateTimeFormatOptions = { 
        year: 'numeric', 
        month: 'short', 
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      };
      return date.toLocaleDateString('en-US', options);
    } catch (error) {
      return dateString;
    }
  };

  // Status info configuration with consistent icon sizes
  const getStatusInfo = (status: string) => {
    const statusMap: Record<string, { color: string, icon: JSX.Element, label: string }> = {
      'draft': {
        color: 'secondary',
        icon: <FontAwesomeIcon icon={faFile} style={{ fontSize: 22 }} />,
        label: t("inventoryHistory.status.draft")
      },
      'assigned': {
        color: 'primary',
        icon: <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="16.5" y1="9.4" x2="7.5" y2="4.21"></line><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path><polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline><line x1="12" y1="22.08" x2="12" y2="12"></line></svg>,
        label: t("inventoryHistory.status.assigned")
      },
      'done': {
        color: 'success',
        icon: <FontAwesomeIcon icon={faCheck} style={{ fontSize: 22 }} />,
        label: t("inventoryHistory.status.done")
      },
      'cancel': {
        color: 'danger',
        icon: <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>,
        label: t("inventoryHistory.status.cancelled")
      }
    };
    
    return statusMap[status.toLowerCase()] || {
      color: 'secondary',
      icon: <FontAwesomeIcon icon={faBoxOpen} style={{ fontSize: 22 }} />,
      label: status
    };
  };
  
  // Move type info configuration with consistent icon sizes
  const getMoveTypeInfo = (moveType: string) => {
    const moveTypeMap: Record<string, { color: string, icon: JSX.Element, label: string }> = {
      'in': {
        color: 'success',
        icon: <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><polyline points="19 12 12 19 5 12"></polyline></svg>,
        label: t("inventoryHistory.moveType.in")
      },
      'out': {
        color: 'danger',
        icon: <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="19" x2="12" y2="5"></line><polyline points="5 12 12 5 19 12"></polyline></svg>,
        label: t("inventoryHistory.moveType.out")
      }
    };
    
    return moveTypeMap[moveType.toLowerCase()] || {
      color: 'info',
      icon: <FontAwesomeIcon icon={faBoxOpen} style={{ fontSize: 22 }} />,
      label: moveType || 'Unknown'
    };
  };

  // Fetch inventory data using GraphQL
  const fetchInventory = useCallback(async () => {
    console.log('fetchInventory called with inventoryId:', inventoryId);
    
    if (!inventoryId) {
      console.error('No inventory ID provided');
      setError('No inventory ID provided');
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      console.log('Making GraphQL query to getInventoryById with ID:', inventoryId);
      await getInventoryById({
        variables: {
          inventory_id: inventoryId
        }
      });
      
    } catch (error) {
      console.error('Error fetching inventory:', error);
      setError('An error occurred while loading inventory data');
      toast.error('Error loading inventory details');
    }
  }, [inventoryId, getInventoryById]);
  
  // Process GraphQL inventory data when it changes
  useEffect(() => {
    console.log('Processing inventory data effect', { inventoryLoading, hasData: !!inventoryData });
    
    if (!inventoryLoading && inventoryData) {
      try {
        const result = inventoryData.getInventoryById;
        console.log('GraphQL response:', result);
        
        if (result.status === 'success' && result.inventory_requests && result.inventory_requests.length > 0) {
          // Get the first inventory request from the response
          const data = result.inventory_requests[0];
          
          // Map Move Type directly from API if available
          if (data["Move Type"] && !data.move_type) {
            data.move_type = data["Move Type"];
          }
          
          console.log('GraphQL data to be used:', data);
          setInventory(data);
          
          // Extract product IDs to fetch product details
          if (data.inventory_list && Array.isArray(data.inventory_list)) {
            const productIds = data.inventory_list
              .map((item: InventoryItem) => item.product_id || item.ID)
              .filter((id: string | undefined): id is string => !!id);
            
            if (productIds.length > 0) {
              console.log('Fetching details for product IDs:', productIds);
              fetchProductDetails(productIds);
            }
          }
          
          setLoading(false);
        } else {
          console.error('Invalid GraphQL response:', result);
          setError('Failed to fetch inventory data');
          setLoading(false);
        }
      } catch (error) {
        console.error('Error processing GraphQL response:', error);
        setError('Error processing inventory data');
        setLoading(false);
      }
    } else if (!inventoryLoading && inventoryError) {
      console.error('GraphQL error:', inventoryError);
      setError(inventoryError.message || 'Error fetching inventory data');
      setLoading(false);
    }
  }, [inventoryData, inventoryLoading, inventoryError, fetchProductDetails]);


  // Initialize
  useEffect(() => {
    fetchInventory();
  }, [fetchInventory]);

  // Loading state
  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">{t("inventoryDetails.loading")}</p>
      </Container>
    );
  }

  // Error state
  if (error || !inventory) {
    return (
      <Container className="py-5">
        <Alert variant="danger">
          {error || t("inventoryDetails.notFound")}
        </Alert>
        <div className="text-center mt-4">
          <button 
            className="btn btn-primary"
            onClick={() => navigate('/apps/e-commerce/admin/inventory-history')}
          >
            {t("inventoryDetails.backToList")}
          </button>
        </div>
      </Container>
    );
  }

  // Check if can edit (only draft status can be edited)
  const canEdit = inventory.status.toLowerCase() === 'draft';

  return (
    <Container className="py-4">
      <div className="bg-body">
        {/* Header with back button and title */}
        <InventoryHeader 
          inventoryId={inventory.inv_id} 
          status={inventory.status}
          stockType={
            inventory.stock_type  === undefined 
              ? "FBW" // Default value when undefined
              : typeof inventory.stock_type === 'boolean'
                ? (inventory.stock_type ? "FBW" : "drop")
                : String(inventory.stock_type) // Convert to string safely
          }
        />
        
        <div className="mt-3">
          {/* Status and move type */}
          <StatusCard
            status={inventory.status}
            statusInfo={getStatusInfo(inventory.status)}
            moveType={inventory.move_type || 'in'}
            moveTypeInfo={getMoveTypeInfo(inventory.move_type || 'in')}
            stockType={
              inventory.stock_type === undefined 
                ? "FBW" // Default value when undefined
                : typeof inventory.stock_type === 'boolean'
                  ? (inventory.stock_type ? "FBW" : "drop")
                  : String(inventory.stock_type) // Convert to string safely
            }
          />
          
          {/* Date information */}
          <DateInfoCard
            requestedDate={inventory.date_demande || ''}
            scheduledDate={inventory.date_prevue || ''}
            formatDate={formatDate}
          />
          
          {/* Product list */}
          <ProductsListCard
            items={inventory.inventory_list.map((item: InventoryItem) => {
              const productId = item.product_id || item.ID;
              const productDetail = productId ? productDetails[productId] : undefined;
              
              return {
                ...item,
                productName: productDetail?.name || `Product ${productId}`,
                imageUrl: productDetail?.imageUrl || 'https://via.placeholder.com/50',
                currentStock: productDetail?.stockQty || 0,
                seller_reference: productDetail?.seller_reference || 'No REF'
              };
            })}
            title={t("inventoryDetails.products")}
          />
          
          {productsLoading && (
            <div className="text-center my-3">
              <Spinner animation="border" variant="primary" size="sm" className="me-2" />
              <span className="text-body-secondary">{t("inventoryDetails.loadingProducts")}</span>
            </div>
          )}
          
          {/* Action buttons */}
          <ActionButtons
            canEdit={canEdit}
            onEdit={() => setEditMode(true)}
          />
          
          {/* Edit Modal */}
          {editMode && inventory && (
            <EditInventoryRequestModal
              show={editMode}
              onHide={() => setEditMode(false)}
              request={{
                inv_id: inventory.inv_id,
                date: inventory['date_prevue'] || inventory['date prévue'] || '',
                scheduledDate: inventory.date_prevue,
                status: inventory.status,
                "Move Type": inventory.move_type || "in", // Ensure Move Type is properly passed
                "Stock Type": inventory.stock_type,
                "date Demandé": inventory.date_demande,
                inventory_list: inventory.inventory_list.map((item: InventoryItem) => {
                  const productId = item.product_id || item.ID;
                  const productDetail = productId ? productDetails[productId] : undefined;
                  
                  return {
                    ID: item.ID || productId,
                    Qty: item.Qty || (item.quantity_done?.toString() || '0'),
                    product_id: productId,
                    quantity_done: item.quantity_done || parseInt(item.Qty || '0'),
                    productName: productDetail?.name || `Product ${productId}`,
                    currentStock: productDetail?.stockQty || 0,
                    imageUrl: productDetail?.imageUrl || 'https://via.placeholder.com/50',
                    seller_reference: productDetail?.seller_reference || 'No REF'
                  };
                })
              }}
              onRequestUpdated={fetchInventory}
            />
          )}
        </div>
      </div>
    </Container>
  );
};

export default InventoryDetailsPage;
